import { useState, useMemo, useRef, useEffect } from "react";
// eslint-disable-next-line no-unused-vars
import { HiOutlineViewGrid, HiOutlineViewList } from "react-icons/hi";
import { HiOutlineQuestionMarkCircle } from "react-icons/hi2";
import { Header, Loader } from "../../components";
import { useAuthContext } from "../../context/auth";
import { LEER_ACTIVIDAD_PRECALCULADA } from "../../utils/getActividadPrecalculada";
import { useHubHeaderFilters } from "../Ventas/HubHeaderContext";
import TablaCalorAgencias from "./components/TablaCalorAgencias";
import TablaCalorClientes from "./components/TablaCalorClientes";
import { useMapaCalorAgenciasData } from "./hooks/useMapaCalorAgenciasData";
import { useMapaCalorClientesData } from "./hooks/useMapaCalorClientesData";
import { useMapaCalorData, UMBRALES } from "./hooks/useMapaCalorData";
import styles from "./MapaCalor.module.scss";

const COLOR_PRIORITY = { rojo: 3, amarillo: 2, verde: 1 };

// Orden de categorías top por nombre (más robusto que por ID)
const CATEGORIAS_TOP_NAMES = ["AUTOMOTRIZ", "BANCA & FINANZAS", "EDUCACIÓN", "TELCOM", "TECNOLOGÍA"];

const getTopOrder = (nombre) => {
  const idx = CATEGORIAS_TOP_NAMES.indexOf((nombre || "").toUpperCase().trim());
  return idx !== -1 ? idx : undefined;
};

const MapaCalor = ({ renderHeader, tabKey } = {}) => {
  const { pais } = useAuthContext();
  const { data, loading, categorias: rawCategorias, _raw, refetch, actividadCalculadoAt } = useMapaCalorData(pais);

  // Toggle principal: Agencias vs Clientes (default: agencias)
  const [modo, setModo] = useState("agencias");

  // Datos agencias (reutiliza los mismos datos crudos)
  const { data: dataAgencias, usuariosPorAgencia } = useMapaCalorAgenciasData(
    _raw.clientes,
    _raw.usuarios,
    _raw.intermediarios,
    _raw.actividades,
    rawCategorias,
    _raw.contactosAgencia
  );

  // Datos clientes (reutiliza los mismos datos crudos)
  const { data: dataClientes } = useMapaCalorClientesData(
    _raw.clientes,
    _raw.usuarios,
    rawCategorias,
    _raw.actividades,
    _raw.contactosCliente,
    _raw.subcategoriasDB
  );

  // Randomizar orden: Big Six primero (random entre ellas), resto después (random)
  // El orden se calcula una sola vez y se guarda en ref para estabilidad entre refetches
  const shuffleOrderRef = useRef(null);
  const dataAgenciasRandom = useMemo(() => {
    if (!dataAgencias || dataAgencias.length === 0) return dataAgencias;
    const BIG_SIX = ["HAVAS", "OMNICOM", "IPG", "DENTSU", "PUBLICIS", "WPP"];
    const isBigSix = (name) => BIG_SIX.some((b) => (name || "").toUpperCase().includes(b));
    // Generar orden de shuffle solo la primera vez
    if (!shuffleOrderRef.current) {
      const bigNames = dataAgencias.filter((h) => isBigSix(h.nombre)).map((h) => h.nombre);
      const restNames = dataAgencias.filter((h) => !isBigSix(h.nombre)).map((h) => h.nombre);
      const shuffle = (arr) => {
        const a = [...arr];
        for (let i = a.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [a[i], a[j]] = [a[j], a[i]];
        }
        return a;
      };
      shuffleOrderRef.current = [...shuffle(bigNames), ...shuffle(restNames)];
    }
    const order = shuffleOrderRef.current;
    return [...dataAgencias].sort((a, b) => {
      const iA = order.indexOf(a.nombre);
      const iB = order.indexOf(b.nombre);
      return (iA === -1 ? 999 : iA) - (iB === -1 ? 999 : iB);
    });
  }, [dataAgencias]);

  const [editMode, setEditMode] = useState(false);
  const [vistaCards, setVistaCards] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState("A");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroCliente, setFiltroCliente] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [filtroSubcategoria, setFiltroSubcategoria] = useState("");
  const [filtroHolding, setFiltroHolding] = useState("");
  const [filtroAgenciaHija, setFiltroAgenciaHija] = useState("");
  const [filtroClienteAg, setFiltroClienteAg] = useState("");
  const [filtroEjecutivoAg, setFiltroEjecutivoAg] = useState("");
  const [filtroCategoriaCl, setFiltroCategoriaCl] = useState("");
  const [filtroEstadoCl, setFiltroEstadoCl] = useState("");
  const [filtroEmpresaCl, setFiltroEmpresaCl] = useState("");
  const [filtroContactoCl, setFiltroContactoCl] = useState("");
  const [showLeyenda, setShowLeyenda] = useState(false);
  const leyendaRef = useRef(null);

  // Categorías disponibles: top 5 primero, separador, resto alfabético
  const categoriasDisponibles = useMemo(() => {
    const cats = data.map((cat) => ({ id: cat.id, nombre: cat.nombre }));
    const top = cats.filter((c) => getTopOrder(c.nombre) !== undefined);
    top.sort((a, b) => getTopOrder(a.nombre) - getTopOrder(b.nombre));
    const rest = cats.filter((c) => getTopOrder(c.nombre) === undefined);
    rest.sort((a, b) => a.nombre.localeCompare(b.nombre));
    return { top, rest };
  }, [data]);

  // Subcategorías disponibles SOLO para la categoría seleccionada
  const subcategoriasDisponibles = useMemo(() => {
    if (!filtroCategoria) return [];
    const cat = data.find((c) => String(c.id) === filtroCategoria);
    if (!cat || !cat.subcategorias) return [];
    return cat.subcategorias.map((s) => s.nombre).sort();
  }, [data, filtroCategoria]);

  // Clientes (empresas) disponibles para el dropdown — funciona con o sin categoría
  const clientesDisponibles = useMemo(() => {
    let cats = filtroCategoria ? data.filter((c) => String(c.id) === filtroCategoria) : data;
    if (filtroSubcategoria) {
      cats = cats.map((cat) => ({
        ...cat,
        subcategorias: (cat.subcategorias || []).filter((s) => s.nombre === filtroSubcategoria),
      }));
    }
    const nombres = new Set();
    cats.forEach((cat) =>
      (cat.subcategorias || []).forEach((sub) => (sub.marcas || []).forEach((m) => nombres.add(m.nombre)))
    );
    return [...nombres].sort();
  }, [data, filtroCategoria, filtroSubcategoria]);

  const haySubcategorias =
    subcategoriasDisponibles.length > 1 ||
    (subcategoriasDisponibles.length === 1 && subcategoriasDisponibles[0] !== "General");

  // Requiere subcategoría si la categoría tiene subcategorías reales
  // eslint-disable-next-line no-unused-vars
  const _requiereSubcategoria = filtroCategoria && haySubcategorias && !filtroSubcategoria;

  // ─── Filtrado Clientes (vista legacy, mantenida por compatibilidad) ────────
  // eslint-disable-next-line no-unused-vars
  const _dataFiltrada = useMemo(() => {
    if (!filtroCategoria && !filtroCliente && !filtroTipo && !filtroEstado) return [];

    let resultado;
    if (filtroCategoria) {
      if (haySubcategorias && !filtroSubcategoria && !filtroTipo && !filtroEstado) return [];
      resultado = data.filter((cat) => String(cat.id) === filtroCategoria);
    } else {
      resultado = [...data];
    }

    if (filtroSubcategoria) {
      resultado = resultado
        .map((cat) => ({
          ...cat,
          subcategorias: cat.subcategorias.filter((sub) => sub.nombre === filtroSubcategoria),
          clientes: cat.clientes.filter((cli) => cli.subcategoria === filtroSubcategoria),
        }))
        .filter((cat) => cat.clientes.length > 0);
    }

    if (filtroEstado) {
      resultado = resultado
        .map((cat) => {
          const subs = (cat.subcategorias || [])
            .map((sub) => {
              const marcasFiltradas = (sub.marcas || [])
                .map((m) => {
                  const prods = m.productos.filter((p) => p.color === filtroEstado);
                  if (prods.length === 0) return null;
                  const worst = prods.reduce(
                    (w, p) => ((COLOR_PRIORITY[p.color] || 0) > (COLOR_PRIORITY[w] || 0) ? p.color : w),
                    "verde"
                  );
                  return { ...m, productos: prods, color: worst };
                })
                .filter(Boolean);
              if (marcasFiltradas.length === 0) return null;
              const allProds = marcasFiltradas.flatMap((m) => m.productos);
              const worst = allProds.reduce(
                (w, p) => ((COLOR_PRIORITY[p.color] || 0) > (COLOR_PRIORITY[w] || 0) ? p.color : w),
                "verde"
              );
              return { ...sub, marcas: marcasFiltradas, clientes: allProds, color: worst };
            })
            .filter(Boolean);
          const allClientes = subs.flatMap((s) => s.clientes);
          if (allClientes.length === 0) return null;
          const worst = allClientes.reduce(
            (w, c) => ((COLOR_PRIORITY[c.color] || 0) > (COLOR_PRIORITY[w] || 0) ? c.color : w),
            "verde"
          );
          return { ...cat, subcategorias: subs, clientes: allClientes, color: worst };
        })
        .filter(Boolean);
    }

    if (filtroCliente) {
      resultado = resultado
        .map((cat) => {
          const subs = (cat.subcategorias || [])
            .map((sub) => {
              const marcasFiltradas = (sub.marcas || []).filter((m) => m.nombre === filtroCliente);
              if (marcasFiltradas.length === 0) return null;
              const allProds = marcasFiltradas.flatMap((m) => m.productos);
              return { ...sub, marcas: marcasFiltradas, clientes: allProds, color: sub.color };
            })
            .filter(Boolean);
          if (subs.length === 0) return null;
          const allClientes = subs.flatMap((s) => s.clientes);
          return { ...cat, subcategorias: subs, clientes: allClientes };
        })
        .filter(Boolean);
    }

    if (filtroTipo) {
      resultado = resultado
        .map((cat) => {
          const subs = (cat.subcategorias || [])
            .map((sub) => {
              const marcasFiltradas = (sub.marcas || [])
                .map((m) => {
                  const prods = m.productos.filter((p) => p.categoria_cliente === filtroTipo);
                  if (prods.length === 0) return null;
                  return { ...m, productos: prods, color: m.color };
                })
                .filter(Boolean);
              if (marcasFiltradas.length === 0) return null;
              const allProds = marcasFiltradas.flatMap((m) => m.productos);
              return { ...sub, marcas: marcasFiltradas, clientes: allProds, color: sub.color };
            })
            .filter(Boolean);
          if (subs.length === 0) return null;
          const allClientes = subs.flatMap((s) => s.clientes);
          return { ...cat, subcategorias: subs, clientes: allClientes };
        })
        .filter(Boolean);
    }

    return resultado;
  }, [data, filtroCategoria, filtroSubcategoria, filtroEstado, filtroCliente, filtroTipo, haySubcategorias]);

  // ─── Filtrado Agencias ─────────────────────────────────────────────────────
  const holdingsDisponibles = useMemo(() => dataAgenciasRandom.map((h) => h.nombre).sort(), [dataAgenciasRandom]);

  // Agencias hijas disponibles (todas si no hay holding, o solo las del holding seleccionado)
  const agenciasHijasDisponibles = useMemo(() => {
    const source = filtroHolding ? dataAgenciasRandom.filter((h) => h.nombre === filtroHolding) : dataAgenciasRandom;
    const nombres = new Set();
    source.forEach((h) => h.agencias.forEach((ag) => nombres.add(ag.nombre)));
    return [...nombres].sort();
  }, [dataAgenciasRandom, filtroHolding]);

  const dataAgenciasFiltrada = useMemo(() => {
    if (!filtroHolding && !filtroAgenciaHija && !filtroEstado && !filtroClienteAg && !filtroEjecutivoAg)
      return dataAgenciasRandom;
    let result = [...dataAgenciasRandom];
    if (filtroHolding) {
      result = result.filter((h) => h.nombre === filtroHolding);
    }
    if (filtroAgenciaHija) {
      result = result
        .map((h) => {
          const agencias = h.agencias.filter((ag) => ag.nombre === filtroAgenciaHija);
          if (agencias.length === 0) return null;
          return { ...h, agencias, totalClientes: agencias.reduce((s, a) => s + a.totalClientes, 0) };
        })
        .filter(Boolean);
    }
    if (filtroEstado) {
      result = result
        .map((h) => {
          const agencias = h.agencias
            .map((ag) => {
              const equipos = ag.equipos
                .map((eq) => {
                  const clis = eq.clientes.filter((c) => c.colorAgencia === filtroEstado);
                  if (clis.length === 0) return null;
                  return { ...eq, clientes: clis };
                })
                .filter(Boolean);
              if (equipos.length === 0) return null;
              const total = equipos.reduce((s, e) => s + e.clientes.length, 0);
              return { ...ag, equipos, totalClientes: total };
            })
            .filter(Boolean);
          if (agencias.length === 0) return null;
          return { ...h, agencias, totalClientes: agencias.reduce((s, a) => s + a.totalClientes, 0) };
        })
        .filter(Boolean);
    }
    // Filtro por nombre de cliente (empresa/producto)
    if (filtroClienteAg) {
      const term = filtroClienteAg.toLowerCase();
      result = result
        .map((h) => {
          const agencias = h.agencias
            .map((ag) => {
              const equipos = ag.equipos
                .map((eq) => {
                  const clis = eq.clientes.filter(
                    (c) =>
                      (c.empresa || "").toLowerCase().includes(term) || (c.producto || "").toLowerCase().includes(term)
                  );
                  if (clis.length === 0) return null;
                  return { ...eq, clientes: clis };
                })
                .filter(Boolean);
              if (equipos.length === 0) return null;
              const total = equipos.reduce((s, e) => s + e.clientes.length, 0);
              return { ...ag, equipos, totalClientes: total };
            })
            .filter(Boolean);
          if (agencias.length === 0) return null;
          return { ...h, agencias, totalClientes: agencias.reduce((s, a) => s + a.totalClientes, 0) };
        })
        .filter(Boolean);
    }
    // Filtro por ejecutivo (miembros del equipo)
    if (filtroEjecutivoAg) {
      const term = filtroEjecutivoAg.toLowerCase();
      result = result
        .map((h) => {
          const agencias = h.agencias
            .map((ag) => {
              const equipos = ag.equipos.filter((eq) => {
                const miembros = [eq.director, ...eq.miembros].filter(Boolean);
                return miembros.some((m) => `${m.nombre} ${m.apellido}`.toLowerCase().includes(term));
              });
              if (equipos.length === 0) return null;
              const total = equipos.reduce((s, e) => s + e.clientes.length, 0);
              return { ...ag, equipos, totalClientes: total };
            })
            .filter(Boolean);
          if (agencias.length === 0) return null;
          return { ...h, agencias, totalClientes: agencias.reduce((s, a) => s + a.totalClientes, 0) };
        })
        .filter(Boolean);
    }
    return result;
  }, [dataAgenciasRandom, filtroHolding, filtroAgenciaHija, filtroEstado, filtroClienteAg, filtroEjecutivoAg]);

  // ─── Categorías disponibles para vista Clientes ───────────────────────────
  const categoriasClienteDisponibles = useMemo(() => {
    const cats = dataClientes.map((cat) => ({ id: cat.id, nombre: cat.nombre }));
    const top = cats.filter((c) => getTopOrder(c.nombre) !== undefined);
    top.sort((a, b) => getTopOrder(a.nombre) - getTopOrder(b.nombre));
    const rest = cats.filter((c) => getTopOrder(c.nombre) === undefined);
    rest.sort((a, b) => a.nombre.localeCompare(b.nombre));
    return { top, rest };
  }, [dataClientes]);

  // ─── Filtrado vista Clientes ──────────────────────────────────────────────
  const dataClientesFiltrada = useMemo(() => {
    // Helper: filtrar empresas dentro de subcategorías
    const filterSubs = (subs, filterEmpFn) =>
      subs
        .map((sub) => {
          const empresas = filterEmpFn(sub.empresas);
          if (empresas.length === 0) return null;
          return { ...sub, empresas, totalClientes: empresas.reduce((s, e) => s + e.totalProductos, 0) };
        })
        .filter(Boolean);

    const rebuildCat = (cat, subs) => {
      if (subs.length === 0) return null;
      return {
        ...cat,
        subcategorias: subs,
        soloGeneral: subs.length === 1 && subs[0].nombre === "General",
        totalClientes: subs.reduce((s, sub) => s + sub.totalClientes, 0),
      };
    };

    let result = [...dataClientes];
    if (filtroCategoriaCl) {
      result = result.filter((cat) => String(cat.id) === filtroCategoriaCl);
    }
    if (filtroEstadoCl) {
      result = result
        .map((cat) => {
          const subs = filterSubs(cat.subcategorias, (emps) =>
            emps
              .map((emp) => {
                const prods = emp.productos.filter((p) => p.colorCliente === filtroEstadoCl);
                if (prods.length === 0) return null;
                return { ...emp, productos: prods, totalProductos: prods.length };
              })
              .filter(Boolean)
          );
          return rebuildCat(cat, subs);
        })
        .filter(Boolean);
    }
    if (filtroEmpresaCl) {
      const term = filtroEmpresaCl.toLowerCase();
      result = result
        .map((cat) => {
          const subs = filterSubs(cat.subcategorias, (emps) =>
            emps.filter(
              (emp) =>
                emp.nombre.toLowerCase().includes(term) ||
                emp.productos.some((p) => (p.producto || "").toLowerCase().includes(term))
            )
          );
          return rebuildCat(cat, subs);
        })
        .filter(Boolean);
    }
    if (filtroContactoCl) {
      const term = filtroContactoCl.toLowerCase();
      result = result
        .map((cat) => {
          const subs = filterSubs(cat.subcategorias, (emps) =>
            emps.filter((emp) =>
              emp.productos.some((p) =>
                (p.contactosEquipo || []).some((c) => `${c.nombre} ${c.apellido}`.toLowerCase().includes(term))
              )
            )
          );
          return rebuildCat(cat, subs);
        })
        .filter(Boolean);
    }
    return result;
  }, [dataClientes, filtroCategoriaCl, filtroEstadoCl, filtroEmpresaCl, filtroContactoCl]);

  // Click-outside para cerrar leyenda
  useEffect(() => {
    if (!showLeyenda) return;
    const handler = (e) => {
      if (leyendaRef.current && !leyendaRef.current.contains(e.target)) setShowLeyenda(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showLeyenda]);

  // Regla del hub (10.250/ventas-hub.md): el header (logo+grid+filtros) es del shell y queda
  // inmaculado al cambiar de tab — nunca desaparece mientras la tab carga. Antes esto cortaba acá
  // con `return <Loader fullPage />` ANTES de construir los filtros → el header nunca llegaba a
  // publicarse mientras loading=true. El loading gate se mueve adentro del área de contenido
  // (abajo, `.wrapper`): el header se sigue armando/publicando siempre, el loader queda contenido
  // debajo — nunca a pantalla completa (10.177: cargando ≠ sin datos, ver Commit 2).

  // ─── Toggle Clientes / Agencias ────────────────────────────────────────────
  const modoToggle = (
    <div style={{ display: "flex", border: "1px solid #ccc", borderRadius: 6, overflow: "hidden", marginRight: 15 }}>
      <button
        className={`${styles.toggleBtn} ${modo === "agencias" ? styles.active : ""}`}
        onClick={() => setModo("agencias")}
        type="button"
        style={{ borderRadius: 0, border: "none", fontSize: ".78rem", padding: "4px 12px" }}
      >
        Agencias
      </button>
      <button
        className={`${styles.toggleBtn} ${modo === "clientes" ? styles.active : ""}`}
        onClick={() => setModo("clientes")}
        type="button"
        style={{
          borderRadius: 0,
          border: "none",
          borderLeft: "1px solid #ccc",
          fontSize: ".78rem",
          padding: "4px 12px",
        }}
      >
        Clientes
      </button>
    </div>
  );

  // ─── Filtros Clientes (vista legacy, mantenida por compatibilidad) ──────────
  // eslint-disable-next-line no-unused-vars
  const _filtrosClientes = (
    <>
      {modoToggle}
      <select
        className={`${styles.filterSelect} ${filtroTipo ? styles.activeFilter : ""}`}
        value={filtroTipo}
        onChange={(e) => setFiltroTipo(e.target.value)}
      >
        <option value="">Tipo</option>
        <option value="A">Cliente A</option>
        <option value="B">Cliente B</option>
        <option value="C">Cliente C</option>
      </select>

      <select
        className={`${styles.filterSelect} ${filtroEstado ? styles.activeFilter : ""}`}
        value={filtroEstado}
        onChange={(e) => setFiltroEstado(e.target.value)}
      >
        <option value="">Estado</option>
        <option value="verde">Verde</option>
        <option value="amarillo">Amarillo</option>
        <option value="rojo">Rojo</option>
      </select>

      <select
        className={`${styles.filterSelect} ${filtroCliente ? styles.activeFilter : ""}`}
        value={filtroCliente}
        onChange={(e) => setFiltroCliente(e.target.value)}
      >
        <option value="">Cliente</option>
        {clientesDisponibles.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>

      <select
        className={`${styles.filterSelect} ${filtroCategoria ? styles.activeFilter : ""}`}
        value={filtroCategoria}
        onChange={(e) => {
          setFiltroCategoria(e.target.value);
          setFiltroSubcategoria("");
        }}
      >
        <option value="">Categoría</option>
        {categoriasDisponibles.top.map((c) => (
          <option key={c.id} value={c.id}>
            {c.nombre}
          </option>
        ))}
        {categoriasDisponibles.top.length > 0 && categoriasDisponibles.rest.length > 0 && (
          <option disabled>──────────</option>
        )}
        {categoriasDisponibles.rest.map((c) => (
          <option key={c.id} value={c.id}>
            {c.nombre}
          </option>
        ))}
      </select>

      <select
        className={`${styles.filterSelect} ${filtroSubcategoria ? styles.activeFilter : ""}`}
        value={filtroSubcategoria}
        onChange={(e) => setFiltroSubcategoria(e.target.value)}
        disabled={!filtroCategoria || !haySubcategorias}
      >
        <option value="">Subcategoría</option>
        {subcategoriasDisponibles.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>

      <button
        className={`${styles.toggleBtn} ${!vistaCards ? styles.active : ""}`}
        onClick={() => setVistaCards(false)}
        type="button"
        title="Vista tabla"
      >
        <HiOutlineViewList size={16} />
      </button>
      <button
        className={`${styles.toggleBtn} ${vistaCards ? styles.active : ""}`}
        onClick={() => setVistaCards(true)}
        type="button"
        title="Vista cards"
      >
        <HiOutlineViewGrid size={16} />
      </button>
    </>
  );

  // ─── Filtros Vista Clientes (nueva) ─────────────────────────────────────────
  const filtrosVistaClientes = (
    <>
      {modoToggle}
      <select
        className={`${styles.filterSelect} ${filtroCategoriaCl ? styles.activeFilter : ""}`}
        value={filtroCategoriaCl}
        onChange={(e) => setFiltroCategoriaCl(e.target.value)}
      >
        <option value="">Categoría</option>
        {categoriasClienteDisponibles.top.map((c) => (
          <option key={c.id} value={c.id}>
            {c.nombre}
          </option>
        ))}
        {categoriasClienteDisponibles.top.length > 0 && categoriasClienteDisponibles.rest.length > 0 && (
          <option disabled>──────────</option>
        )}
        {categoriasClienteDisponibles.rest.map((c) => (
          <option key={c.id} value={c.id}>
            {c.nombre}
          </option>
        ))}
      </select>

      <select
        className={`${styles.filterSelect} ${filtroEstadoCl ? styles.activeFilter : ""}`}
        value={filtroEstadoCl}
        onChange={(e) => setFiltroEstadoCl(e.target.value)}
      >
        <option value="">Estado</option>
        <option value="verde">Verde</option>
        <option value="amarillo">Amarillo</option>
        <option value="rojo">Rojo</option>
      </select>

      <input
        type="text"
        placeholder="Buscar empresa..."
        value={filtroEmpresaCl}
        onChange={(e) => setFiltroEmpresaCl(e.target.value)}
        className={styles.filterSelect}
        style={{ minWidth: 120, fontSize: ".78rem" }}
      />

      <input
        type="text"
        placeholder="Buscar contacto..."
        value={filtroContactoCl}
        onChange={(e) => setFiltroContactoCl(e.target.value)}
        className={styles.filterSelect}
        style={{ minWidth: 120, fontSize: ".78rem" }}
      />
    </>
  );

  // ─── Filtros Agencias ──────────────────────────────────────────────────────
  const filtrosAgencias = (
    <>
      {modoToggle}
      <select
        className={`${styles.filterSelect} ${filtroHolding ? styles.activeFilter : ""}`}
        value={filtroHolding}
        onChange={(e) => {
          setFiltroHolding(e.target.value);
          setFiltroAgenciaHija("");
        }}
      >
        <option value="">Holding</option>
        {holdingsDisponibles.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>

      <select
        className={`${styles.filterSelect} ${filtroAgenciaHija ? styles.activeFilter : ""}`}
        value={filtroAgenciaHija}
        onChange={(e) => {
          const val = e.target.value;
          setFiltroAgenciaHija(val);
          // Auto-seleccionar holding si se elige agencia sin holding previo
          if (val && !filtroHolding) {
            const parent = dataAgenciasRandom.find((h) => h.agencias.some((ag) => ag.nombre === val));
            if (parent) setFiltroHolding(parent.nombre);
          }
        }}
      >
        <option value="">Agencia</option>
        {agenciasHijasDisponibles.map((a) => (
          <option key={a} value={a}>
            {a}
          </option>
        ))}
      </select>

      <select
        className={`${styles.filterSelect} ${filtroEstado ? styles.activeFilter : ""}`}
        value={filtroEstado}
        onChange={(e) => setFiltroEstado(e.target.value)}
      >
        <option value="">Estado</option>
        <option value="verde">Verde</option>
        <option value="amarillo">Amarillo</option>
        <option value="rojo">Rojo</option>
      </select>

      <input
        type="text"
        placeholder="Buscar cliente..."
        value={filtroClienteAg}
        onChange={(e) => setFiltroClienteAg(e.target.value)}
        className={styles.filterSelect}
        style={{ minWidth: 120, fontSize: ".78rem" }}
      />

      <input
        type="text"
        placeholder="Buscar ejecutivo..."
        value={filtroEjecutivoAg}
        onChange={(e) => setFiltroEjecutivoAg(e.target.value)}
        className={styles.filterSelect}
        style={{ minWidth: 120, fontSize: ".78rem" }}
      />

      <button
        onClick={() => setEditMode((v) => !v)}
        style={{
          padding: "4px 12px",
          borderRadius: 6,
          border: editMode ? "2px solid #0d6efd" : "1px solid #aaa",
          backgroundColor: editMode ? "#0d6efd" : "#fff",
          color: editMode ? "#fff" : "#333",
          fontSize: ".78rem",
          fontWeight: 600,
          cursor: "pointer",
          marginLeft: 8,
        }}
      >
        {editMode ? "✎ Editando" : "✎ Editar"}
      </button>
    </>
  );

  const Badges = ({ umbral }) => (
    <span className={styles.resumenBloque}>
      <span className={`${styles.badge} ${styles.badgeVerde}`}>{`≤${umbral.verde}d`}</span>
      <span className={`${styles.badge} ${styles.badgeAmarillo}`}>{`≤${umbral.amarillo}d`}</span>
      <span className={`${styles.badge} ${styles.badgeRojo}`}>{`>${umbral.amarillo}d`}</span>
    </span>
  );

  const LeyendaFila = ({ tier, label }) => (
    <div className={styles.resumenFila}>
      <span className={styles.resumenTier}>{label} →</span>
      <span className={styles.resumenTipo}>Agencia</span>
      <Badges umbral={UMBRALES[tier].agencia} />
      <span className={styles.resumenTipo}>Cliente</span>
      <Badges umbral={UMBRALES[tier].cliente} />
    </div>
  );

  const helpButton = (
    <div ref={leyendaRef} style={{ position: "relative" }}>
      <button
        className={styles.toggleBtn}
        onClick={() => setShowLeyenda((v) => !v)}
        type="button"
        title="Umbrales de semáforo"
      >
        <HiOutlineQuestionMarkCircle size={18} />
      </button>
      {showLeyenda && (
        <div className={styles.leyendaPopover}>
          <LeyendaFila label="Cliente A" tier="A" />
          <LeyendaFila label="Cliente B" tier="B" />
          <LeyendaFila label="Cliente C" tier="C" />
        </div>
      )}
    </div>
  );

  // Marca discreta de la corrida del cron que alimenta la actividad precalculada (Fase 3B paso 3).
  // Si nunca corrió (calculado_at null tras terminar de cargar), lo dice — no se queda en blanco
  // en silencio (10.177).
  const horaCalculado = (iso) => new Date(iso).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" });
  const actualizadoMark = LEER_ACTIVIDAD_PRECALCULADA && !loading && (
    <span style={{ fontSize: "0.72rem", color: "#999", whiteSpace: "nowrap", marginRight: "8px" }}>
      {actividadCalculadoAt
        ? `Actividad actualizada ${horaCalculado(actividadCalculadoAt)}`
        : "Actividad: el cálculo automático todavía no corrió"}
    </span>
  );

  // Header compartido: los filtros (según modo) + el toggle Agencias/Clientes + el helpButton
  // viajan JUNTOS en un solo body. Dentro del hub (tabKey presente), el header es del SHELL — acá
  // solo se PUBLICAN los filtros (useHubHeaderFilters, hook incondicional, corre en TODO render,
  // cargando o no: el header nunca depende de llegar a este punto del árbol). Fuera del hub
  // (standalone, sin tabKey), se arma un <Header> propio como siempre — renderHeader si vino, o
  // uno con expandChildren. El toggle sigue siendo interno de Mapa de Calor.
  const filtrosDelModo = modo === "clientes" ? filtrosVistaClientes : filtrosAgencias;
  const headerBody = (
    <>
      {filtrosDelModo}
      {actualizadoMark}
      {helpButton}
    </>
  );
  useHubHeaderFilters(tabKey, headerBody);
  const headerElement = tabKey ? null : renderHeader ? (
    renderHeader(headerBody)
  ) : (
    <Header expandChildren>{headerBody}</Header>
  );

  // ─── Render modo Clientes ──────────────────────────────────────────────────
  if (modo === "clientes") {
    // Vista combinada: si se busca una empresa específica, mostrar también su agencia
    const empresaBuscada = filtroEmpresaCl.trim().toLowerCase();
    const mostrarAgencia = empresaBuscada.length >= 3;
    let agenciaDelCliente = [];
    if (mostrarAgencia) {
      // Buscar clientes que matchean la empresa y filtrar agencias que los contienen
      agenciaDelCliente = dataAgenciasRandom
        .map((h) => {
          const agencias = h.agencias
            .map((ag) => {
              const equipos = ag.equipos
                .map((eq) => {
                  const clis = eq.clientes.filter(
                    (c) =>
                      (c.empresa || "").toLowerCase().includes(empresaBuscada) ||
                      (c.producto || "").toLowerCase().includes(empresaBuscada)
                  );
                  if (clis.length === 0) return null;
                  return { ...eq, clientes: clis };
                })
                .filter(Boolean);
              if (equipos.length === 0) return null;
              const total = equipos.reduce((s, e) => s + e.clientes.length, 0);
              return { ...ag, equipos, totalClientes: total };
            })
            .filter(Boolean);
          if (agencias.length === 0) return null;
          return { ...h, agencias, totalClientes: agencias.reduce((s, a) => s + a.totalClientes, 0) };
        })
        .filter(Boolean);
    }

    return (
      // Regresión 2026-09-21 (marco de página): con tabKey (dentro del hub) el marco de 15px ya
      // lo aporta el shell (Ventas.module.scss .wrapper) — acá se anula el propio para no
      // duplicarlo. Standalone (sin tabKey) conserva su padding:15px de siempre.
      <div style={{ padding: tabKey ? 0 : 15 }}>
        {headerElement}
        <div className={styles.wrapper}>
          {loading ? (
            <div style={{ minHeight: 300 }}>
              <Loader />
            </div>
          ) : (
            <>
              {mostrarAgencia && (
                <div
                  style={{
                    margin: "0 0 8px",
                    fontSize: ".85rem",
                    fontWeight: 700,
                    color: "#555",
                    borderBottom: "2px solid #ccc",
                    paddingBottom: 6,
                  }}
                >
                  Cliente
                </div>
              )}
              <TablaCalorClientes data={dataClientesFiltrada} />
              {mostrarAgencia && agenciaDelCliente.length > 0 && (
                <>
                  <div
                    style={{
                      margin: "24px 0 8px",
                      fontSize: ".85rem",
                      fontWeight: 700,
                      color: "#555",
                      borderBottom: "2px solid #ccc",
                      paddingBottom: 6,
                    }}
                  >
                    Agencia
                  </div>
                  <TablaCalorAgencias data={agenciaDelCliente} editMode={false} allUsuarios={_raw.usuarios} />
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // ─── Render modo Agencias ──────────────────────────────────────────────────
  return (
    // Mismo criterio del marco de página que el modo Clientes arriba.
    <div style={{ padding: tabKey ? 0 : 15 }}>
      {headerElement}
      <div className={styles.wrapper}>
        {loading ? (
          <div style={{ minHeight: 300 }}>
            <Loader />
          </div>
        ) : (
          <TablaCalorAgencias
            data={dataAgenciasFiltrada}
            editMode={editMode}
            usuariosPorAgencia={usuariosPorAgencia}
            allUsuarios={_raw.usuarios}
            onRefetch={refetch}
          />
        )}
      </div>
    </div>
  );
};

export default MapaCalor;
